/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { ACTIVE_APP_SESSION_KEY, NAV_ITEM_BY_ID, navPathById } from '../constants/appSuites'
import { useNavVisibility } from '../hooks/useNavVisibility'
import type { AppId, AppSuiteApp, LauncherApp, LauncherAppId, NavItemId } from '../types/appSuites'
import { adminShellApp, resolveShellAppId } from '../utils/appSuites/adminShellApp'
import { resolveActiveApp } from '../utils/appSuites/resolveActiveApp'
import { resolveLanding } from '../utils/appSuites/resolveLanding'
import { suggestedAppFor } from '../utils/appSuites/suggestedApp'
import { useAppSuiteSettings } from './AppSuiteSettingsContext'
import { useAuthValue } from './AuthContext'

interface ActiveAppValue {
  apps: LauncherApp[]
  activeApp: LauncherApp | null
  accessibleApps: LauncherApp[]
  lockedApps: AppSuiteApp[]
  suggestedId: AppId | null
  landingFor: (appId: LauncherAppId) => string | null
  switchApp: (appId: LauncherAppId) => void
  goToLauncher: () => void
}

const EMPTY: ActiveAppValue = {
  apps: [],
  activeApp: null,
  accessibleApps: [],
  lockedApps: [],
  suggestedId: null,
  landingFor: () => null,
  switchApp: () => undefined,
  goToLauncher: () => undefined,
}

const EMPTY_APPS: AppSuiteApp[] = []

const ActiveAppContext = createContext<ActiveAppValue>(EMPTY)

function readSticky(): LauncherAppId | null {
  try {
    const stored = sessionStorage.getItem(ACTIVE_APP_SESSION_KEY)
    if (
      stored === 'context' ||
      stored === 'ideate' ||
      stored === 'studio' ||
      stored === 'observability' ||
      stored === 'admin'
    ) {
      return stored
    }
  } catch {
    return null
  }
  return null
}

function productSticky(stickyId: LauncherAppId | null): AppId | null {
  return stickyId === 'admin' ? null : stickyId
}

export function ActiveAppProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { experience, suite } = useAppSuiteSettings()
  const { isItemVisible } = useNavVisibility()
  const { currentUser } = useAuthValue()
  const location = useLocation()
  const navigate = useNavigate()
  const [stickyId, setStickyId] = useState<LauncherAppId | null>(readSticky)
  const pathById = useMemo(() => navPathById(), [])
  const apps = suite?.apps ?? EMPTY_APPS
  const appsOn = experience === 'apps'

  const itemVisible = useCallback(
    (itemId: string) => {
      const item = NAV_ITEM_BY_ID[itemId as NavItemId]
      return item ? isItemVisible(item) : false
    },
    [isItemVisible],
  )

  const showAdmin = appsOn && (itemVisible('admin') || itemVisible('platform_admin'))
  const adminLanding = itemVisible('admin') ? '/admin' : '/platform-admin'
  const adminApp = useMemo(
    () => (showAdmin ? adminShellApp(t('appSuites.admin.name'), t('appSuites.admin.tagline'), adminLanding) : null),
    [adminLanding, showAdmin, t],
  )
  const accessibleApps = useMemo(() => {
    if (!appsOn) return EMPTY_APPS
    const open = apps.filter((app) => app.enabled && resolveLanding(app, itemVisible, pathById))
    return adminApp ? [...open, adminApp] : open
  }, [adminApp, apps, appsOn, itemVisible, pathById])
  const lockedApps = useMemo(
    () => apps.filter((app) => app.enabled && !accessibleApps.some((entry) => entry.id === app.id)),
    [accessibleApps, apps],
  )
  const suggestedId = suggestedAppFor(currentUser?.roles ?? [], suite?.role_suggestions ?? [])
  const productId = appsOn
    ? resolveActiveApp(
        location.pathname,
        apps,
        productSticky(stickyId),
        suggestedId,
        accessibleApps.flatMap((app) => (app.id === 'admin' ? [] : [app.id])),
        pathById,
      )
    : null
  const activeAppId = appsOn ? resolveShellAppId(location.pathname, Boolean(adminApp), productId) : null

  if (appsOn && activeAppId && activeAppId !== stickyId) {
    setStickyId(activeAppId)
  }

  useEffect(() => {
    if (!activeAppId) return
    try {
      sessionStorage.setItem(ACTIVE_APP_SESSION_KEY, activeAppId)
    } catch {
      /* session storage can be unavailable */
    }
  }, [activeAppId])

  const landingFor = useCallback(
    (appId: LauncherAppId) => {
      if (appId === 'admin') return adminApp?.landing_path ?? null
      const app = apps.find((entry) => entry.id === appId)
      return app ? resolveLanding(app, itemVisible, pathById) : null
    },
    [adminApp, apps, itemVisible, pathById],
  )

  const switchApp = useCallback(
    (appId: LauncherAppId) => {
      setStickyId(appId)
      try {
        sessionStorage.setItem(ACTIVE_APP_SESSION_KEY, appId)
      } catch {
        /* ignore */
      }
      const path = landingFor(appId)
      if (path) navigate(path)
    },
    [landingFor, navigate],
  )

  const goToLauncher = useCallback(() => {
    navigate('/')
  }, [navigate])

  const value = useMemo<ActiveAppValue>(() => {
    if (!appsOn) return EMPTY
    return {
      apps: adminApp ? [...apps.filter((app) => app.enabled), adminApp] : apps.filter((app) => app.enabled),
      activeApp:
        activeAppId === 'admin'
          ? adminApp
          : apps.find((app) => app.id === activeAppId) ?? null,
      accessibleApps,
      lockedApps,
      suggestedId,
      landingFor,
      switchApp,
      goToLauncher,
    }
  }, [adminApp, appsOn, apps, activeAppId, accessibleApps, lockedApps, suggestedId, landingFor, switchApp, goToLauncher])

  return <ActiveAppContext.Provider value={value}>{children}</ActiveAppContext.Provider>
}

export function useActiveApp(): ActiveAppValue {
  return useContext(ActiveAppContext)
}
